/**
 * A moeda UCC desenhada: disco dourado com aro e o "U" do escudo cunhado no
 * centro. Serve de ícone em qualquer tamanho; o gradiente usa um id por
 * instância para duas moedas na mesma tela não dividirem a definição.
 */

import { useId } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/cn';
import { useAuth } from '@/lib/auth';
import { moedasApi } from '@/lib/moedas';

export function MoedaUcc({ className }: { className?: string }) {
  const id = useId().replace(/:/g, '');
  return (
    <svg viewBox="0 0 32 32" className={cn('shrink-0', className)} aria-hidden>
      <defs>
        <linearGradient id={`m-face-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ffe375" />
          <stop offset="0.45" stopColor="#f8d428" />
          <stop offset="1" stopColor="#b8860b" />
        </linearGradient>
        <linearGradient id={`m-aro-${id}`} x1="1" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff2b0" />
          <stop offset="1" stopColor="#6b4e08" />
        </linearGradient>
      </defs>
      <circle cx="16" cy="16" r="15" fill={`url(#m-aro-${id})`} />
      <circle cx="16" cy="16" r="12.2" fill={`url(#m-face-${id})`} />
      <circle cx="16" cy="16" r="10.4" fill="none" stroke="#6b4e08" strokeOpacity="0.45" strokeWidth="0.8" strokeDasharray="1.2 1.4" />
      <path d="M11.2 10.2v7.1a4.8 4.8 0 0 0 9.6 0v-7.1h-2.7v7.1a2.1 2.1 0 0 1-4.2 0v-7.1z" fill="#5a4106" />
    </svg>
  );
}

/**
 * Saldo do time no cabeçalho: aparece para quem capitaneia um time e leva à
 * loja. Com mais de um time, soma os saldos e diz quantos são.
 */
export function SaldoNoCabecalho() {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ['moedas', 'meus-times'],
    queryFn: moedasApi.meusTimes,
    enabled: Boolean(user),
    staleTime: 30_000,
  });
  if (!data || data.times.length === 0) return null;
  const total = data.times.reduce((t, x) => t + x.saldo, 0);
  const rotulo = data.times.length === 1 ? data.times[0]!.name : `${data.times.length} times`;
  return (
    <Link
      to="/loja"
      title={`Moedas de ${rotulo} — abrir a loja`}
      className="group inline-flex items-center gap-2 rounded-lg border border-gold/35 bg-gold/[0.07] py-1.5 pr-3 pl-2 transition-colors hover:border-gold/70 hover:bg-gold/[0.12] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
    >
      <MoedaUcc className="size-5 transition-transform duration-300 group-hover:rotate-[20deg]" />
      <span className="tabular text-sm font-bold text-gold-ink">{total.toLocaleString('pt-BR')}</span>
      <span className="sr-only">moedas</span>
    </Link>
  );
}
